import React, { useEffect, useState, useCallback } from 'react';
import { gccFlowsService } from '../../services/gccFlowsService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import PlotlyChart from '../../components/charts/PlotlyChart';
import CollapsibleSection from '../signals/components/CollapsibleSection';

const MARKETS = ['ADX', 'DFM', 'Qatar', 'Kuwait', 'Saudi Arabia'];
const PERIODS = [
  { label: '1D', value: '1d' },
  { label: 'WTD', value: 'wtd' },
  { label: 'MTD', value: 'mtd' },
  { label: 'YTD', value: 'ytd' },
];

const fmt = (v, dp = 2) => {
  if (v == null || isNaN(v)) return '-';
  const n = Number(v);
  return n.toLocaleString(undefined, { maximumFractionDigits: dp });
};

const signClass = (n) => (n == null ? '' : Number(n) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400');

const MarketFlowsPage = ({ filterParams }) => {
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topTickers, setTopTickers] = useState({ inflows: [], outflows: [] });
  const [period, setPeriod] = useState('ytd');
  const [topMarket, setTopMarket] = useState('');
  const [cumMetric, setCumMetric] = useState('usd'); // 'usd' | 'pct_mcap'
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, tr, tt] = await Promise.all([
        gccFlowsService.getMarketOverview(),
        gccFlowsService.getMarketTrend(filterParams),
        gccFlowsService.getTopTickers({ period, market: topMarket || undefined }),
      ]);
      setOverview(ov.data);
      setTrend(tr.data || []);
      setTopTickers(tt.data || { inflows: [], outflows: [] });
    } finally {
      setLoading(false);
    }
  }, [filterParams, period, topMarket]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build cumulative trend chart traces — switch field by toggle
  const yField = cumMetric === 'pct_mcap' ? 'cumulative_pct_mcap' : 'cumulative_usd';
  const traces = MARKETS.map((m) => {
    const rows = trend.filter((r) => r.market === m);
    return {
      x: rows.map((r) => r.report_date),
      y: rows.map((r) => r[yField]),
      name: m,
      type: 'scatter',
      mode: 'lines',
      line: { width: 2 },
      hovertemplate: cumMetric === 'pct_mcap'
        ? `<b>${m}</b><br>%{x}<br>Cum. net: %{y:.4f}% of mcap<extra></extra>`
        : `<b>${m}</b><br>%{x}<br>Cum. net: $%{y:,.1f}mn<extra></extra>`,
    };
  }).filter((t) => t.x.length > 0);

  if (loading && !overview) return <LoadingSpinner message="Loading GCC market flows..." />;

  return (
    <div>
      {/* KPI tiles */}
      <CollapsibleSection title="Latest Snapshot — net foreign $mn">
        {!overview?.markets?.length ? (
          <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
            No data yet. Upload daily files via the Ingestion tab.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {overview.markets.map((m) => (
              <div key={m.market} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{m.market}</div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-2">{m.report_date}</div>
                {[
                  { label: '1D', v: m.net_usd_1d },
                  { label: 'WTD', v: m.net_usd_wtd },
                  { label: 'MTD', v: m.net_usd_mtd },
                  { label: 'YTD', v: m.net_usd_ytd },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between items-baseline text-xs py-0.5">
                    <span className="text-gray-500 dark:text-gray-400">{row.label}</span>
                    <span className={`tabular-nums font-medium ${signClass(row.v)}`}>
                      {row.v == null ? '-' : `${Number(row.v) >= 0 ? '+' : ''}${fmt(row.v, 1)}`}
                    </span>
                  </div>
                ))}
                <div className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">{m.ticker_count} tickers</div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* Cumulative trend */}
      <CollapsibleSection title={`Cumulative Net Foreign Flow (${cumMetric === 'pct_mcap' ? '% of Mcap' : '$mn'})`}>
        <div className="flex gap-1 mb-3">
          {[
            { label: '$ mn', value: 'usd' },
            { label: '% of Mcap', value: 'pct_mcap' },
          ].map((m) => (
            <button
              key={m.value}
              onClick={() => setCumMetric(m.value)}
              className={`text-xs px-2.5 py-1 rounded-md ${
                cumMetric === m.value
                  ? 'bg-[#c9a96e] text-white'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {traces.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No data in selected period.</div>
        ) : (
          <PlotlyChart
            data={traces}
            layout={{
              showlegend: true,
              legend: { orientation: 'h', y: -0.2 },
              hovermode: 'x unified',
              margin: { t: 20, b: 80, l: 50, r: 20 },
              yaxis: {
                title: cumMetric === 'pct_mcap' ? '% of mcap (cumulative)' : '$ mn (cumulative)',
                zeroline: true,
                tickformat: cumMetric === 'pct_mcap' ? '.3f' : ',.0f',
              },
              xaxis: { title: '' },
            }}
          />
        )}
      </CollapsibleSection>

      {/* Top inflow / outflow tickers */}
      <CollapsibleSection title="Top Inflow / Outflow Tickers (latest report)">
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`text-xs px-2.5 py-1 rounded-md ${
                  period === p.value
                    ? 'bg-[#c9a96e] text-white'
                    : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <select
            value={topMarket}
            onChange={(e) => setTopMarket(e.target.value)}
            className="text-xs px-2.5 py-1 rounded-md bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300"
          >
            <option value="">All markets</option>
            {MARKETS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TopList title={`Top Inflows (${period.toUpperCase()})`} rows={topTickers.inflows} />
          <TopList title={`Top Outflows (${period.toUpperCase()})`} rows={topTickers.outflows} />
        </div>
      </CollapsibleSection>
    </div>
  );
};

const TopList = ({ title, rows }) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
    <div className="bg-gray-50 dark:bg-[#0d1117] px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
      {title}
    </div>
    {rows.length === 0 ? (
      <div className="text-center py-6 text-xs text-gray-500 dark:text-gray-400">No data</div>
    ) : (
      <table className="w-full text-xs">
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400 w-8 tabular-nums">{i + 1}</td>
              <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200 font-medium">{r.ticker}</td>
              <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400 text-[10px]">{r.market}</td>
              <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${signClass(r.net_usd)}`}>
                {r.net_usd == null ? '-' : `${Number(r.net_usd) >= 0 ? '+' : ''}${fmt(r.net_usd, 2)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default MarketFlowsPage;
